// AiIntakeBar — Hybrid vehicle selector
// Primary: pill-based Model → Year → Fitment click flow (no AI endpoint needed)
// Secondary: text input that hits the AI Worker for natural language parsing

const INTAKE_ENDPOINT = 'https://formyauto-ai-intake.jonesingtonllc.workers.dev';
const CURRENT_YEAR = new Date().getFullYear();
const RECENT_YEAR_COUNT = 5;

// Models in display order — used to group raw model strings like "Model X 6-Seat"
const BASE_MODELS = ['Model S', 'Model 3', 'Model X', 'Model Y', 'Cybertruck'];

// Model → silhouette image filename (must match assets)
const MODEL_IMAGES = {
  'Model 3': 'model-3.png',
  'Model Y': 'model-y.png',
  'Model S': 'model-s.png',
  'Model X': 'model-x.png',
  'Cybertruck': 'model-cybertruck.png',
};

// Extract base model from a raw model string (e.g. "Model X 6-Seat" → "Model X")
function getBaseModel(raw) {
  const lower = raw.toLowerCase();
  for (const base of BASE_MODELS) {
    if (lower.startsWith(base.toLowerCase())) return base;
  }
  return raw; // fallback: use as-is
}

// Fitment display labels
const FITMENT_LABELS = {
  'standard': 'Standard',
  'highland': 'Highland',
  'juniper': 'Juniper',
  '5-seater': '5 Seater',
  '5-seat': '5 Seater',
  '6-seater': '6 Seater',
  '6-seat': '6 Seater',
  '6-seater-console': '6 Seater (Console)',
  '7-seater': '7 Seater',
  '7-seat': '7 Seater',
  '7-seater-non-folding': '7 Seater (Non-Folding)',
  'non-folding-7-seat': '7 Seater (Non-Folding)',
  'folding-7-seat': '7 Seater (Folding)',
  'multi-fitment': 'Multi Fitment',
};

const THINKING_MESSAGES = [
  'Checking the garage...',
  'Let me look that up...',
  'One sec...',
];

const SEARCHING_MESSAGES = [
  'Matching you with the right floor mats...',
  'Checking what we\'ve got for you...',
  'Pulling up your perfect fit...',
];

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function buildHandle(year, make, model, fitment, trim) {
  const parts = [
    String(year),
    make.trim().toLowerCase(),
    model.trim().toLowerCase().replace(/ /g, '-'),
  ];
  if (trim) parts.push(trim.trim().toLowerCase());
  parts.push(fitment.trim().toLowerCase());
  return parts.join('-');
}

// Returns trim options when (year, baseModel) requires a trim split, else null.
// Currently only 2026+ Tesla Model Y splits into Juniper (LR/Performance —
// deeper frunk well) and Standard (smaller frunk; partial kit until U-Ace
// releases the Standard-fit frunk SKU).
function getTrimsFor(year, baseModel) {
  if (!year || !baseModel) return null;
  if (baseModel === 'Model Y' && year >= 2026) {
    return [
      { value: 'juniper', label: 'Juniper (Long Range / Performance)' },
      { value: 'standard', label: 'Standard' },
    ];
  }
  return null;
}

function esc(text) {
  const d = document.createElement('div');
  d.textContent = text;
  return d.innerHTML;
}

function getAssetBase() {
  const header = document.querySelector('.site-header[data-asset-base]');
  return header ? header.dataset.assetBase : '';
}

class AiIntakeBar extends HTMLElement {
  constructor() {
    super();
    this._vehicles = [];
    this._selectedModel = null;
    this._selectedYear = null;
    this._selectedTrim = null;
    this._selectedFitment = null;
    this._loaded = false;
    this._targetSlot = 0; // which garage slot to fill (0 = primary, 1 = second vehicle)
    this._modelRevealTimer = null;
  }

  connectedCallback() {
    this._storefrontToken = this.dataset.storefrontToken || '';
    this._storefrontUrl = this.dataset.storefrontUrl || 'https://020a3a.myshopify.com/api/2025-10/graphql.json';
    this.innerHTML = this._renderShell();
    this._loadVehicles();
  }

  // ---- Shell ----

  _renderShell() {
    return `
      <div class="ai-intake">
        <div class="ai-intake__steps" data-ref="steps"></div>
        <div class="ai-intake__status" data-ref="status" hidden></div>
        <div class="ai-intake__result" data-ref="result" hidden></div>
        <div class="ai-intake__fallback" data-ref="fallback" hidden></div>
        <div class="ai-intake__text-mode" data-ref="text-mode" hidden>
          <form class="ai-intake__input-row" data-ref="form">
            <svg class="ai-intake__icon" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              class="ai-intake__input"
              data-ref="input"
              type="text"
              placeholder="e.g. M1TL0001309 or L1TL01301509"
              autocomplete="off"
              required
            />
            <button class="ai-intake__submit" data-ref="submit" type="submit">
              Go
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </form>
        </div>
        <div class="ai-intake__toggle" data-ref="toggle" hidden>
          <button class="ai-intake__toggle-btn" data-ref="toggle-btn" type="button">or find a specific SKU</button>
        </div>
        <p class="ai-intake__disclaimer" data-ref="disclaimer">Images are representative only — always verify fitment for your specific year and trim.</p>
      </div>
    `;
  }

  _ref(name) {
    return this.querySelector(`[data-ref="${name}"]`);
  }

  // ---- Data loading ----

  async _loadVehicles() {
    const query = `
      query AllVehicles {
        metaobjects(type: "vehicle", first: 250) {
          nodes {
            handle
            fields { key value }
          }
        }
      }
    `;

    try {
      const res = await fetch(this._storefrontUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this._storefrontToken,
        },
        body: JSON.stringify({ query }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const nodes = data?.data?.metaobjects?.nodes || [];

      this._vehicles = nodes.map((node) => {
        const fields = {};
        (node.fields || []).forEach((f) => { fields[f.key] = f.value; });
        const model = fields.model || '';
        return {
          handle: node.handle,
          year: parseInt(fields.year, 10),
          make: fields.make || '',
          model: model,
          baseModel: getBaseModel(model),
          fitment: fields.fitment || '',
        };
      }).filter((v) => v.year && v.make && v.model && v.fitment);

      this._loaded = true;
      this._showModelStep();
      document.dispatchEvent(new CustomEvent('ai-intake:ready', { detail: this }));
    } catch (err) {
      console.error('[ai-intake]', err);
      // Fall back to text-only mode
      this._showTextMode();
    }
  }

  // ---- Step 1: Model ----

  _showModelStep() {
    // Cancel any pending pill-reveal from a previous _showModelStep call.
    // This prevents double-binding when _showModelStep is called before vehicles
    // are loaded (e.g. from _reset on init) and then called again by _loadVehicles.
    if (this._modelRevealTimer) {
      clearTimeout(this._modelRevealTimer);
      this._modelRevealTimer = null;
    }

    const baseModels = [...new Set(this._vehicles.map((v) => v.baseModel))];
    const sorted = BASE_MODELS.filter((m) => baseModels.includes(m));
    baseModels.forEach((m) => { if (!sorted.includes(m)) sorted.push(m); });

    const stepsEl = this._ref('steps');
    const assetBase = getAssetBase();

    // Build shimmer placeholders (same count as models, single rect each)
    const shimmersHtml = sorted.map(() => `<div class="ai-intake__shimmer"></div>`).join('');

    // Build real pills (hidden initially)
    const pillsHtml = sorted.map((m) => {
      const imgFile = MODEL_IMAGES[m];
      const imgHtml = (assetBase && imgFile) ? `<img src="${assetBase}${imgFile}" alt="" class="ai-intake__pill-img">` : '';
      return `<button class="ai-intake__pill ai-intake__pill--model" type="button" data-value="${esc(m)}">${imgHtml}${esc(m)}</button>`;
    }).join('');

    stepsEl.innerHTML = `
      <div class="ai-intake__step" data-step="model">
        <div class="ai-intake__step-label">${this._targetSlot === 1 ? 'What\'s the 2nd car in your garage?' : '<s class="ai-intake__step-struck">2020: What do you drive?</s><br>2026: What drives <em>you</em> around? 😂'}</div>
        <div class="ai-intake__pills" data-ref="shimmer-row">${shimmersHtml}</div>
        <div class="ai-intake__pills" data-ref="pills-row" style="display:none">${pillsHtml}</div>
      </div>
    `;

    this._bindToggle();

    // Fade in the step (shows shimmers)
    const step = this.querySelector('[data-step="model"]');
    requestAnimationFrame(() => step.classList.add('ai-intake__step--visible'));

    // After 2s shimmer, swap to real pills with sequential reveal
    this._modelRevealTimer = setTimeout(async () => {
      this._modelRevealTimer = null;
      const shimmerRow = this.querySelector('[data-ref="shimmer-row"]');
      const pillsRow = this.querySelector('[data-ref="pills-row"]');
      if (!pillsRow) return; // step was replaced before timer fired
      if (shimmerRow) shimmerRow.remove();
      pillsRow.style.display = '';

      // Sequential reveal: each pill pops in one by one
      const pills = pillsRow.querySelectorAll('.ai-intake__pill');
      for (const pill of pills) {
        await delay(150);
        pill.classList.add('ai-intake__pill--visible');
      }

      // Bind click handlers after reveal
      pills.forEach((btn) => {
        btn.addEventListener('click', () => {
          this._selectPill(btn);
          this._selectedModel = btn.dataset.value;
          this._showYearStep();
        });
      });

      // Show the "or just type your vehicle" toggle
      const toggle = this._ref('toggle');
      if (toggle) toggle.hidden = false;
    }, 2000);
  }

  // ---- Step 2: Year ----

  async _showYearStep() {
    const years = [...new Set(
      this._vehicles.filter((v) => v.baseModel === this._selectedModel).map((v) => v.year)
    )].sort((a, b) => b - a);

    const recentYears = years.filter((y) => y >= CURRENT_YEAR - RECENT_YEAR_COUNT + 1);
    const olderYears = years.filter((y) => y < CURRENT_YEAR - RECENT_YEAR_COUNT + 1);

    const stepsEl = this._ref('steps');

    // Fade out current step
    await this._fadeOutCurrentStep();

    const assetBase = getAssetBase();
    const modelImg = MODEL_IMAGES[this._selectedModel];
    const modelImgHtml = (assetBase && modelImg) ? `<img src="${assetBase}${modelImg}" alt="${esc(this._selectedModel)}" class="ai-intake__selected-img">` : '';
    stepsEl.innerHTML = `
      <div class="ai-intake__step" data-step="year">
        ${modelImgHtml}
        <div class="ai-intake__step-context">${esc(this._selectedModel)}</div>
        <div class="ai-intake__step-label">What year?</div>
        <div class="ai-intake__pills">
          ${recentYears.map((y) => `<button class="ai-intake__pill" type="button" data-value="${y}">${y}</button>`).join('')}
          ${olderYears.length ? `
            <div class="ai-intake__year-dropdown-wrap">
              <button class="ai-intake__year-trigger" type="button" aria-expanded="false">
                Older...
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true"><path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <div class="ai-intake__year-list" hidden>
                ${olderYears.map((y) => `<button class="ai-intake__year-option" type="button" data-value="${y}">${y}</button>`).join('')}
              </div>
            </div>
          ` : ''}
        </div>
        <button class="ai-intake__back" type="button" data-back="model">&larr; Back</button>
      </div>
    `;

    await this._fadeInStep('year');

    stepsEl.querySelectorAll('.ai-intake__pill').forEach((btn) => {
      btn.addEventListener('click', () => {
        this._selectPill(btn);
        this._selectedYear = parseInt(btn.dataset.value, 10);
        this._afterYearSelected();
      });
    });

    const yearTrigger = stepsEl.querySelector('.ai-intake__year-trigger');
    const yearList = stepsEl.querySelector('.ai-intake__year-list');
    if (yearTrigger && yearList) {
      yearTrigger.addEventListener('click', () => {
        const open = !yearList.hidden;
        yearList.hidden = open;
        yearTrigger.setAttribute('aria-expanded', String(!open));
      });
      yearList.querySelectorAll('.ai-intake__year-option').forEach((opt) => {
        opt.addEventListener('click', () => {
          yearList.hidden = true;
          yearTrigger.setAttribute('aria-expanded', 'false');
          this._selectedYear = parseInt(opt.dataset.value, 10);
          this._afterYearSelected();
        });
      });
    }

    stepsEl.querySelector('.ai-intake__back').addEventListener('click', () => {
      this._selectedModel = null;
      this._selectedYear = null;
      this._selectedTrim = null;
      this._selectedFitment = null;
      this._fadeOutCurrentStep().then(() => this._showModelStep());
    });
  }

  _afterYearSelected() {
    // Trim-required vehicles (currently 2026+ Model Y) get an extra step
    // before fitment so the customer picks Juniper vs Standard explicitly.
    const trims = getTrimsFor(this._selectedYear, this._selectedModel);
    if (trims) {
      this._showTrimStep(trims);
      return;
    }
    const fitments = this._getFitments(this._selectedModel, this._selectedYear);
    if (fitments.length === 1) {
      // Single fitment — skip straight to search
      this._selectedFitment = fitments[0];
      this._doSearch();
    } else {
      this._showFitmentStep(fitments);
    }
  }

  _afterTrimSelected() {
    const fitments = this._getFitments(this._selectedModel, this._selectedYear);
    if (fitments.length === 1) {
      this._selectedFitment = fitments[0];
      this._doSearch();
    } else if (fitments.length === 0) {
      // Should not happen for currently-split vehicles, but fall back to a
      // hardcoded 5-seater rather than blocking the customer.
      this._selectedFitment = '5-seater';
      this._doSearch();
    } else {
      this._showFitmentStep(fitments);
    }
  }

  // ---- Step 2.5: Trim ----

  async _showTrimStep(trims) {
    await this._fadeOutCurrentStep();

    const stepsEl = this._ref('steps');
    const assetBase = getAssetBase();
    const modelImg = MODEL_IMAGES[this._selectedModel];
    const modelImgHtml = (assetBase && modelImg) ? `<img src="${assetBase}${modelImg}" alt="${esc(this._selectedModel)}" class="ai-intake__selected-img">` : '';
    stepsEl.innerHTML = `
      <div class="ai-intake__step" data-step="trim">
        ${modelImgHtml}
        <div class="ai-intake__step-context">${this._selectedYear} ${esc(this._selectedModel)}</div>
        <div class="ai-intake__step-label">Which trim?</div>
        <div class="ai-intake__pills">
          ${trims.map((t) => `<button class="ai-intake__pill" type="button" data-value="${esc(t.value)}">${esc(t.label)}</button>`).join('')}
        </div>
        <button class="ai-intake__back" type="button" data-back="year">&larr; Back</button>
      </div>
    `;

    await this._fadeInStep('trim');

    stepsEl.querySelectorAll('.ai-intake__pill').forEach((btn) => {
      btn.addEventListener('click', () => {
        this._selectPill(btn);
        this._selectedTrim = btn.dataset.value;
        this._afterTrimSelected();
      });
    });

    stepsEl.querySelector('.ai-intake__back').addEventListener('click', () => {
      this._selectedYear = null;
      this._selectedTrim = null;
      this._selectedFitment = null;
      this._fadeOutCurrentStep().then(() => this._showYearStep());
    });
  }

  // ---- Step 3: Fitment ----

  async _showFitmentStep(fitments) {
    await this._fadeOutCurrentStep();

    const stepsEl = this._ref('steps');
    const assetBase = getAssetBase();
    const modelImg = MODEL_IMAGES[this._selectedModel];
    const modelImgHtml = (assetBase && modelImg) ? `<img src="${assetBase}${modelImg}" alt="${esc(this._selectedModel)}" class="ai-intake__selected-img">` : '';
    const trimContext = this._selectedTrim
      ? ` ${this._selectedTrim.charAt(0).toUpperCase() + this._selectedTrim.slice(1)}`
      : '';
    stepsEl.innerHTML = `
      <div class="ai-intake__step" data-step="fitment">
        ${modelImgHtml}
        <div class="ai-intake__step-context">${this._selectedYear} ${esc(this._selectedModel)}${esc(trimContext)}</div>
        <div class="ai-intake__step-label">Which configuration?</div>
        <div class="ai-intake__pills">
          ${fitments.map((f) => `<button class="ai-intake__pill" type="button" data-value="${esc(f)}">${esc(FITMENT_LABELS[f] || f)}</button>`).join('')}
        </div>
        <button class="ai-intake__back" type="button" data-back="year">&larr; Back</button>
      </div>
    `;

    await this._fadeInStep('fitment');

    stepsEl.querySelectorAll('.ai-intake__pill').forEach((btn) => {
      btn.addEventListener('click', () => {
        this._selectPill(btn);
        this._selectedFitment = btn.dataset.value;
        this._doSearch();
      });
    });

    stepsEl.querySelector('.ai-intake__back').addEventListener('click', () => {
      // If a trim was picked, step back to trim selection — otherwise to year.
      this._selectedFitment = null;
      if (this._selectedTrim) {
        const trims = getTrimsFor(this._selectedYear, this._selectedModel);
        this._selectedTrim = null;
        this._fadeOutCurrentStep().then(() => this._showTrimStep(trims));
      } else {
        this._selectedYear = null;
        this._fadeOutCurrentStep().then(() => this._showYearStep());
      }
    });
  }

  // ---- Search animation + result ----

  async _doSearch() {
    await this._fadeOutCurrentStep();

    const stepsEl = this._ref('steps');
    stepsEl.innerHTML = '';

    // Hide toggle and disclaimer
    const toggle = this._ref('toggle');
    const disclaimer = this._ref('disclaimer');
    if (toggle) toggle.hidden = true;
    if (disclaimer) disclaimer.hidden = true;

    const handle = buildHandle(this._selectedYear, 'Tesla', this._selectedModel, this._selectedFitment, this._selectedTrim);
    const trimLabel = this._selectedTrim ? ` ${this._selectedTrim.charAt(0).toUpperCase() + this._selectedTrim.slice(1)}` : '';
    const name = `${this._selectedYear} Tesla ${this._selectedModel}${trimLabel}`;
    const fitmentLabel = FITMENT_LABELS[this._selectedFitment] || this._selectedFitment;

    // Show searching status — paced for smooth, readable transitions
    this._showStatusPanel(this._selectedModel);
    await this._addStatusLine(`${name} — got it!`, { success: true, delayAfter: 1400 });
    await this._addStatusLine(pickRandom(SEARCHING_MESSAGES), { thinking: true, delayAfter: 2200 });
    this._removeThinkingLines();
    await delay(600);

    // Verify vehicle exists in our data
    let vehicle = this._vehicles.find(
      (v) => v.baseModel === this._selectedModel && v.year === this._selectedYear && v.fitment === this._selectedFitment
    );

    // For trim-split vehicles, an underlying "vehicle" metaobject may not
    // exist for every trim (e.g. 2026 Standard — no U-Ace SKUs yet, so no
    // metaobject was synced). Synthesize a minimal one so the customer
    // still lands on the correct family page.
    if (!vehicle && this._selectedTrim) {
      vehicle = {
        baseModel: this._selectedModel,
        year: this._selectedYear,
        make: 'Tesla',
        model: this._selectedModel,
        fitment: this._selectedFitment,
        handle,
      };
    }

    if (!vehicle) {
      this._showFallbackUI(`We found your ${name} but don't have products for it just yet. Check back soon!`);
      return;
    }

    // Persist session into the target garage slot. For trim-split vehicles
    // we save the trim-bearing family handle so future restores land on the
    // right page (Juniper vs Standard).
    if (typeof VehicleSession !== 'undefined') {
      VehicleSession.setSlot(this._targetSlot, {
        year: vehicle.year,
        make: vehicle.make,
        model: vehicle.model,
        fitment: vehicle.fitment,
        trim: this._selectedTrim || null,
        handle: this._selectedTrim ? handle : vehicle.handle,
      });
    }

    // Render inline product family hero
    await this._addStatusLine('Found your kit!', { found: true, delayAfter: 2000 });
    await this._renderInlineResult(this._selectedTrim ? handle : vehicle.handle, name);
  }

  // ---- Helpers ----

  _getFitments(baseModel, year) {
    return [...new Set(
      this._vehicles
        .filter((v) => v.baseModel === baseModel && v.year === year && v.fitment !== 'multi-fitment')
        .map((v) => v.fitment)
    )].sort();
  }

  _selectPill(btn) {
    btn.closest('.ai-intake__pills').querySelectorAll('.ai-intake__pill').forEach((p) => {
      p.classList.remove('ai-intake__pill--selected');
    });
    btn.classList.add('ai-intake__pill--selected');
  }

  // ---- Step transitions ----

  async _fadeInStep(stepName) {
    const step = this.querySelector(`[data-step="${stepName}"]`);
    if (!step) return;
    await delay(30);
    step.classList.add('ai-intake__step--visible');

    // Stagger pills
    const pills = step.querySelectorAll('.ai-intake__pill');
    for (const pill of pills) {
      await delay(60);
      pill.classList.add('ai-intake__pill--visible');
    }
  }

  async _fadeOutCurrentStep() {
    const step = this.querySelector('.ai-intake__step--visible');
    if (!step) return;
    step.classList.remove('ai-intake__step--visible');
    await delay(400);
  }

  // ---- Status panel (shared with text mode) ----

  _showStatusPanel(modelName) {
    const el = this._ref('status');
    el.hidden = false;
    el.innerHTML = '';

    // Show model image during loading if available
    if (modelName) {
      const assetBase = getAssetBase();
      const COMPOSITE_CONFIGS = {
        'Model X': {
          base: 'model-x-base.png',
          wheelFront: 'model-x-wheel-front.png',
          wheelRear: 'model-x-wheel-rear.png',
          modifierClass: 'ai-intake__car-composite--model-x',
        },
        'Cybertruck': {
          base: 'model-cybertruck-base.png',
          wheelFront: 'model-cybertruck-wheel-front.png',
          wheelRear: 'model-cybertruck-wheel-rear.png',
          modifierClass: 'ai-intake__car-composite--cybertruck',
        },
      };

      const config = COMPOSITE_CONFIGS[modelName];
      if (config && assetBase) {
        const composite = document.createElement('div');
        composite.className = 'ai-intake__car-composite ' + config.modifierClass;

        const baseImg = document.createElement('img');
        baseImg.src = assetBase + config.base;
        baseImg.alt = modelName;
        baseImg.className = 'ai-intake__car-base';
        composite.appendChild(baseImg);

        const buildWheel = (filename, modClass) => {
          const wrap = document.createElement('div');
          wrap.className = 'ai-intake__car-wheel ' + modClass;
          const angle = document.createElement('div');
          angle.className = 'ai-intake__car-wheel-angle';
          const img = document.createElement('img');
          img.src = assetBase + filename;
          img.alt = '';
          angle.appendChild(img);
          wrap.appendChild(angle);
          return wrap;
        };
        composite.appendChild(buildWheel(config.wheelRear, 'ai-intake__car-wheel--rear'));
        composite.appendChild(buildWheel(config.wheelFront, 'ai-intake__car-wheel--front'));
        el.appendChild(composite);
      } else {
        const imgFile = MODEL_IMAGES[modelName];
        if (assetBase && imgFile) {
          const img = document.createElement('img');
          img.src = assetBase + imgFile;
          img.alt = modelName;
          img.className = 'ai-intake__status-img';
          el.appendChild(img);
        }
      }
    }

    // Pre-render exactly 2 fixed line slots so the container height never changes
    for (let i = 0; i < 2; i++) {
      const line = document.createElement('div');
      line.className = 'ai-intake__status-line ai-intake__status-line--visible';
      line.textContent = '\u00a0';
      line.dataset.slot = String(i);
      el.appendChild(line);
    }

    requestAnimationFrame(() => el.classList.add('ai-intake--visible'));
  }

  async _addStatusLine(text, opts = {}) {
    const el = this._ref('status');
    // Find the first pre-rendered slot that is currently showing a placeholder
    const slot = [...el.querySelectorAll('.ai-intake__status-line')]
      .find((l) => l.textContent === '\u00a0');

    const line = slot || (() => {
      // Fallback: append (shouldn't happen with 2 pre-rendered slots)
      const l = document.createElement('div');
      l.className = 'ai-intake__status-line';
      el.appendChild(l);
      return l;
    })();

    // Fade out, swap content, fade in — no DOM add/remove so no layout shift
    line.classList.remove('ai-intake__status-line--visible',
      'ai-intake__status-line--success', 'ai-intake__status-line--found',
      'ai-intake__status-line--thinking');
    await delay(200);
    line.textContent = text;
    if (opts.found) line.classList.add('ai-intake__status-line--found');
    else if (opts.success) line.classList.add('ai-intake__status-line--success');
    if (opts.thinking) line.classList.add('ai-intake__status-line--thinking');
    line.classList.add('ai-intake__status-line--visible');
    if (opts.delayAfter) await delay(opts.delayAfter);
  }

  _removeThinkingLines() {
    this._ref('status')
      .querySelectorAll('.ai-intake__status-line--thinking')
      .forEach((el) => {
        el.classList.remove('ai-intake__status-line--visible');
        // Reset slot to placeholder — keeps height, clears text
        setTimeout(() => {
          el.classList.remove('ai-intake__status-line--thinking');
          el.textContent = '\u00a0';
          el.classList.add('ai-intake__status-line--visible');
        }, 300);
      });
  }

  _hideAllPanels() {
    for (const ref of ['status', 'result', 'fallback']) {
      const el = this._ref(ref);
      el.hidden = true;
      el.classList.remove('ai-intake--visible');
    }
  }

  // ---- Text mode (AI endpoint fallback) ----

  _bindToggle() {
    const toggleBtn = this._ref('toggle-btn');
    const toggle = this._ref('toggle');
    if (!toggleBtn || toggleBtn._bound) return;
    toggleBtn._bound = true;

    toggleBtn.addEventListener('click', () => {
      this._showTextMode();
    });
  }

  _showTextMode() {
    const textEl = this._ref('text-mode');
    const toggle = this._ref('toggle');
    const stepsEl = this._ref('steps');

    stepsEl.innerHTML = '';
    if (toggle) toggle.hidden = true;
    if (this._ref('disclaimer')) this._ref('disclaimer').hidden = true;
    textEl.hidden = false;
    requestAnimationFrame(() => textEl.classList.add('ai-intake--visible'));

    const form = this._ref('form');
    if (form && !form._bound) {
      form._bound = true;
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const query = this._ref('input').value.trim();
        if (query) this._submitText(query);
      });
    }

    this._ref('input').focus();
  }

  _looksLikeSku(query) {
    // Match 3D MAXpider SKU patterns: L1TL, E1TL, M1TL, Q1TL followed by digits
    return /^[LEMQ]1TL\d{4,}/i.test(query.trim());
  }

  async _searchBySku(sku) {
    const query = `
      query SearchBySku($q: String!) {
        search(query: $q, first: 1, types: PRODUCT) {
          nodes {
            ... on Product {
              handle
              title
            }
          }
        }
      }
    `;

    const res = await fetch(this._storefrontUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this._storefrontToken,
      },
      body: JSON.stringify({ query, variables: { q: sku } }),
    });
    if (!res.ok) return null;

    const data = await res.json();
    const nodes = data?.data?.search?.nodes || [];
    return nodes.length ? nodes[0] : null;
  }

  async _submitText(query) {
    const textEl = this._ref('text-mode');
    textEl.classList.remove('ai-intake--visible');
    await delay(300);
    textEl.hidden = true;

    this._ref('submit').disabled = true;
    this._ref('input').disabled = true;

    this._showStatusPanel();
    await this._addStatusLine(pickRandom(THINKING_MESSAGES), { thinking: true, delayAfter: 600 });

    // Check if input is a SKU — search Shopify directly
    if (this._looksLikeSku(query)) {
      try {
        const sku = query.trim().toUpperCase();
        this._removeThinkingLines();
        await delay(300);
        await this._addStatusLine(`Looking up SKU ${sku}...`, { thinking: true, delayAfter: 1200 });

        const product = await this._searchBySku(sku);
        this._removeThinkingLines();
        await delay(300);

        if (product) {
          // Show a short label instead of the full U-Ace title
          const shortTitle = product.title.replace(/^3D MAXpider Compatible with /i, '').replace(/TESLA /i, '');
          await this._addStatusLine(`Found it — ${shortTitle}`, { found: true, delayAfter: 1500 });
          window.location.href = `/products/${product.handle}`;
          return;
        } else {
          this._showFallbackUI(`We couldn't find a product matching SKU "${sku}". Double-check the part number and try again.`);
          return;
        }
      } catch (err) {
        console.error('[ai-intake] SKU search failed:', err);
        // Fall through to normal AI flow
      }
    }

    try {
      const res = await fetch(INTAKE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ input: query }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      this._removeThinkingLines();
      await delay(300);
      await this._handleTextResponse(data);
    } catch (err) {
      this._removeThinkingLines();
      await delay(300);
      console.error('[ai-intake]', err);
      this._showFallbackUI("Something went wrong on our end — sorry about that. Give it another shot?");
    }
  }

  async _handleTextResponse(data) {
    switch (data.type) {
      case 'match': {
        const name = `${data.vehicle.year} ${data.vehicle.make} ${data.vehicle.model}`;
        const fitment = data.vehicle.fitment ? data.vehicle.fitment.replace(/-/g, ' ') : '';

        await this._addStatusLine(`${name} — got it!`, { success: true, delayAfter: 1400 });
        await this._addStatusLine(pickRandom(SEARCHING_MESSAGES), { thinking: true, delayAfter: 2200 });
        this._removeThinkingLines();
        await delay(600);

        if (typeof VehicleSession !== 'undefined') {
          VehicleSession.set(data.vehicle);
        }

        await this._addStatusLine('Found your kit!', { found: true, delayAfter: 2000 });
        await this._renderInlineResult(data.vehicle.handle, name);
        break;
      }
      case 'clarification': {
        const partial = data.partial || {};
        const partialName = [partial.year, partial.make, partial.model].filter(Boolean).join(' ');
        if (partialName) {
          await this._addStatusLine(`${partialName} — nice ride.`, { success: true, delayAfter: 600 });
        }
        await this._addStatusLine('Just need one more detail...', { delayAfter: 400 });

        const el = this._ref('fallback');
        el.hidden = false;
        el.innerHTML = `
          <div class="ai-intake__clarify-question">${esc(data.question)}</div>
          <div class="ai-intake__pills">
            ${data.options.map((opt) => `<button class="ai-intake__pill ai-intake__pill--visible" type="button">${esc(opt)}</button>`).join('')}
          </div>
        `;
        requestAnimationFrame(() => el.classList.add('ai-intake--visible'));

        el.querySelectorAll('.ai-intake__pill').forEach((btn) => {
          btn.addEventListener('click', () => {
            const parts = [partial.year, partial.make, partial.model, btn.textContent].filter(Boolean);
            el.hidden = true;
            el.classList.remove('ai-intake--visible');
            this._hideAllPanels();
            this._submitText(parts.join(' '));
          });
        });
        break;
      }
      case 'no_products': {
        const name = `${data.vehicle.year} ${data.vehicle.make} ${data.vehicle.model}`;
        await this._addStatusLine(`${name} — found it!`, { success: true, delayAfter: 800 });
        await delay(400);
        this._showFallbackUI(`We found your ${name} but don't have products for it just yet. Check back soon!`);
        break;
      }
      case 'no_match':
        await delay(400);
        this._showFallbackUI(data.reason || "Hmm, we couldn't quite figure out that vehicle.");
        break;
      default:
        this._showFallbackUI("Something went wrong. Give it another shot?");
    }
  }

  // ---- Inline product family hero ----

  async _renderInlineResult(vehicleHandle, displayName) {
    const mount = document.getElementById('wizard-result-mount');
    if (!mount) {
      // No mount point — fall back to navigation
      window.location.href = '/pages/families/' + vehicleHandle;
      return;
    }

    try {
      const familyData = await this._fetchFamilyByHandle(vehicleHandle);
      if (!familyData) {
        window.location.href = '/pages/families/' + vehicleHandle;
        return;
      }

      // Check bundle availability BEFORE rendering
      // Model S vehicleHandles end in "-standard" but their bundle products don't.
      const isElitect = vehicleHandle.indexOf('-elitect') !== -1;
      const baseHandle = vehicleHandle.replace('-elitect', '').replace(/-standard$/, '');
      const productHandle = baseHandle + '-full-protection-kit' + (isElitect ? '-elitect' : '');
      let bundleAvailable = true;
      let componentAvailability = {};
      try {
        const bundleRes = await fetch('/products/' + productHandle + '.js');
        if (bundleRes.ok) {
          const bundleData = await bundleRes.json();
          const variant = bundleData.variants ? bundleData.variants[0] : null;
          if (variant && variant.available === false) {
            bundleAvailable = false;
          }
        }
        // If bundle is OOS, pre-check each component
        if (!bundleAvailable) {
          let skus = [];
          try { skus = JSON.parse(familyData.skus || '[]'); } catch (e) { skus = []; }
          const checks = skus.map(async (sku) => {
            const handle = typeof sku === 'object' ? (sku.handle || '') : '';
            const skuId = typeof sku === 'object' ? (sku.sku || '') : sku;
            if (!handle) { componentAvailability[skuId] = false; return; }
            try {
              const r = await fetch('/products/' + handle + '.js');
              if (r.ok) {
                const d = await r.json();
                const v = d.variants ? d.variants.find(v => v.sku === skuId) || d.variants[0] : null;
                componentAvailability[skuId] = v ? { available: v.available, variantId: v.id } : false;
              } else {
                componentAvailability[skuId] = false;
              }
            } catch (e) { componentAvailability[skuId] = false; }
          });
          await Promise.all(checks);
        }
      } catch (e) { /* availability check failed — render normally */ }

      // Hide intake bar and homepage intro content
      this.style.display = 'none';
      ['.homepage-eyebrow', '.homepage-headline', '.homepage-sub', '.homepage-picks-label', '.homepage-picks', '.homepage-flank', '.homepage-badge']
        .forEach((sel) => { document.querySelectorAll(sel).forEach(el => el.style.display = 'none'); });

      // Build and inject hero — pass availability data
      mount.innerHTML = this._buildHeroHtml(familyData, vehicleHandle, bundleAvailable, componentAvailability);
      mount.removeAttribute('hidden');
      mount.classList.add('is-visible');

      // Re-init product family hero event handlers
      if (typeof window.initProductFamilyHero === 'function') {
        window.initProductFamilyHero();
      }

      // Bind OOS SKU title clicks to update gallery
      mount.querySelectorAll('.pf-oos-item__title').forEach((titleEl) => {
        const skuId = titleEl.closest('.pf-oos-item').getAttribute('data-sku');
        if (!skuId) return;
        titleEl.classList.add('pf-oos-item__title--clickable');
        titleEl.addEventListener('click', () => {
          mount.querySelectorAll('.pf-oos-item').forEach((item) => item.classList.remove('pf-oos-item--active'));
          titleEl.closest('.pf-oos-item').classList.add('pf-oos-item--active');
          const thumb = document.querySelector('.pf-thumb[data-sku="' + skuId + '"]');
          if (thumb) thumb.click();
        });
      });

      // Bind notify button to open modal (uses function from product-family-page.js)
      const notifyBtn = mount.querySelector('#pf-notify-btn');
      if (notifyBtn && typeof window.openNotifyModal === 'function') {
        notifyBtn.addEventListener('click', () => window.openNotifyModal());
      } else if (notifyBtn) {
        notifyBtn.addEventListener('click', () => {
          // Fallback if openNotifyModal not available yet
          if (typeof window.openNotifyModal === 'function') window.openNotifyModal();
        });
      }

      // Bind OOS individual add-to-cart buttons if rendered
      mount.querySelectorAll('.pf-oos-item__add').forEach((btn) => {
        btn.addEventListener('click', () => {
          const vid = parseInt(btn.getAttribute('data-variant-id'), 10);
          if (!vid) return;
          btn.disabled = true;
          btn.textContent = 'Adding\u2026';
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] }),
          })
            .then((res) => {
              if (!res.ok) throw new Error('Failed');
              return res.json();
            })
            .then(() => {
              btn.textContent = 'Added!';
              btn.classList.add('pf-oos-item__add--success');
              document.dispatchEvent(new CustomEvent('cart:updated'));
              document.dispatchEvent(new CustomEvent('cart:open'));
            })
            .catch(() => {
              btn.textContent = 'Error';
              btn.disabled = false;
            });
        });
      });
    } catch (err) {
      console.error('[ai-intake] inline render failed, navigating:', err);
      window.location.href = '/pages/families/' + vehicleHandle;
    }
  }

  async _fetchFamilyByHandle(vehicleHandle) {
    const query = `
      query FetchFamily($handle: MetaobjectHandleInput!) {
        metaobject(handle: $handle) {
          handle
          fields { key value }
        }
      }
    `;

    const res = await fetch(this._storefrontUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this._storefrontToken,
      },
      body: JSON.stringify({
        query,
        variables: { handle: { handle: vehicleHandle, type: 'product_family' } },
      }),
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const data = await res.json();
    const obj = data.data && data.data.metaobject;
    if (!obj) return null;

    const fields = {};
    (obj.fields || []).forEach((f) => { fields[f.key] = f.value; });
    return fields;
  }

  _escHtml(str) {
    const d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }

  _buildHeroHtml(familyData, vehicleHandle, bundleAvailable = true, componentAvailability = {}) {
    const name = familyData.name || '';
    const heroPrice = familyData.hero_price || '';
    const comparePrice = familyData.compare_price || '';
    const bundleVariantGid = familyData.bundle_variant_gid || '';
    const elitectHandle = familyData.elitect_family_handle || '';
    const kaguHandle = familyData.kagu_family_handle || '';
    const storefrontToken = this._storefrontToken;

    let images = [];
    try { images = JSON.parse(familyData.images || '[]'); } catch (e) { images = []; }
    const productImages = images.filter((img) => img.type === 'primary' || img.type === 'hero' || img.type === 'sku');

    let skus = [];
    try { skus = JSON.parse(familyData.skus || '[]'); } catch (e) { skus = []; }
    const skusJson = JSON.stringify(skus).replace(/"/g, '&quot;');

    let savingsHtml = '';
    const hp = parseFloat(heroPrice);
    const cp = parseFloat(comparePrice);
    if (comparePrice && cp > hp) {
      const saved = (cp - hp).toFixed(2);
      savingsHtml = `<span class="pf-compare-price">$${comparePrice}</span>
        <span class="pf-savings">Save $${saved}</span>`;
    }

    let galleryHtml = '';
    if (productImages.length > 0) {
      const firstImg = productImages[0];
      const skuLabelText = firstImg.title || '';
      const thumbsHtml = productImages.map((img, idx) => `
        <button class="pf-thumb${idx === 0 ? ' is-active' : ''}" data-index="${idx}" data-sku="${img.sku || ''}" data-title="${img.title || ''}" type="button" aria-label="View image ${idx + 1}">
          <img src="${img.url}" alt="${img.sku || name}" loading="${idx === 0 ? 'eager' : 'lazy'}" width="80" height="80" />
        </button>`).join('');

      galleryHtml = `
        <div class="pf-gallery-layout">
          <span class="pf-sku-label" id="pf-sku-label">${skuLabelText}</span>
          <div class="pf-thumbs" id="pf-thumbs">${thumbsHtml}</div>
          <div class="pf-main-image" id="pf-main-image">
            <img id="pf-main-img" src="${firstImg.url}" alt="${name}" />
          </div>
        </div>`;
    } else {
      galleryHtml = `
        <div class="pf-gallery-layout">
          <div class="pf-main-image pf-main-image--placeholder">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/>
            </svg>
            <span class="pf-gallery-placeholder-text">Product images coming soon</span>
          </div>
        </div>`;
    }

    const hasToggle = !!(elitectHandle || kaguHandle);
    const isElitectPage = !!kaguHandle;
    let materialToggleHtml = '';
    if (hasToggle) {
      materialToggleHtml = `
        <div class="pf-material-toggle-row">
          <span class="pf-material-label">Material</span>
          <div class="pf-material-toggle" role="group" aria-label="Select material">
            <button class="pf-material-toggle__btn${isElitectPage ? '' : ' is-active'}" data-material="kagu" type="button">KAGU</button>
            <button class="pf-material-toggle__btn${isElitectPage ? ' is-active' : ''}" data-material="elitect" type="button">ELITECT</button>
          </div>
          <button class="pf-material-info-trigger" id="pf-material-info-trigger" type="button" aria-label="What is the difference?">
            What's the difference?
          </button>
          <div class="pf-material-info-tooltip" id="pf-material-info-tooltip" hidden>
            <p><strong>KAGU Standard</strong> &mdash; Durable rubber, flat profile. Premium protection at the core value tier.</p>
            <p><strong>ELITECT Premium</strong> &mdash; Premium thermoplastic, raised carbon fiber ridges. Enhanced grip and a more refined look.</p>
          </div>
        </div>`;
    }

    const bundleAttr = bundleVariantGid ? ` data-bundle-variant="${bundleVariantGid}"` : '';

    // Build CTA or OOS notice based on pre-checked availability
    let ctaHtml = '';
    if (bundleAvailable) {
      ctaHtml = `
            <div class="pf-cta-group">
              <div class="pf-cta-row">
                <button class="pf-add-kit-btn" id="pf-add-kit-btn" data-skus="${skusJson}"${bundleAttr}>
                  Add Full Kit to Cart
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </button>
                <a href="/pages/families/${vehicleHandle}" class="pf-view-details">View Full Details &rarr;</a>
              </div>
              <div class="pf-add-feedback" id="pf-add-feedback" hidden></div>
            </div>`;
    } else {
      // Build per-SKU availability rows
      const itemsHtml = skus.map((sku) => {
        const skuId = typeof sku === 'object' ? (sku.sku || '') : sku;
        const title = typeof sku === 'object' ? (sku.title || sku.sku) : sku;
        const avail = componentAvailability[skuId];
        let statusHtml = '';
        if (avail && avail.available) {
          statusHtml = `<button class="pf-oos-item__add" data-variant-id="${avail.variantId}" type="button">Add to Cart</button>`;
        } else {
          statusHtml = '<span class="pf-oos-item__oos">Out of Stock</span>';
        }
        return `<div class="pf-oos-item" data-sku="${skuId}">
          <span class="pf-oos-item__title">${this._escHtml(title)}</span>
          <span class="pf-oos-item__status">${statusHtml}</span>
        </div>`;
      }).join('');

      ctaHtml = `
            <div class="pf-cta-group" style="display:none">
              <div class="pf-cta-row">
                <button class="pf-add-kit-btn" id="pf-add-kit-btn" data-skus="${skusJson}"${bundleAttr} disabled>
                  Currently Out of Stock
                </button>
              </div>
            </div>
            <div class="pf-oos-notice">
              <div class="pf-oos-notice__header">
                <span>\uD83E\uDDD0 This full set is temporarily unavailable</span>
              </div>
              <p class="pf-oos-notice__desc">Not all pieces are in stock right now, but you can still grab what\u2019s available individually. We\u2019ll restock the full set soon!</p>
              <div class="pf-oos-notice__items">${itemsHtml}</div>
              <button class="pf-notify-btn" id="pf-notify-btn" type="button">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M22 17H2a3 3 0 003-3V9a7 7 0 0114 0v5a3 3 0 003 3zm-8.27 4a2 2 0 01-3.46 0"/></svg>
                Notify me when the full set is back
              </button>
            </div>`;
    }

    return `
      <section class="pf-hero" data-elitect-handle="${elitectHandle}" data-kagu-handle="${kaguHandle}" data-family-handle="${vehicleHandle}" data-storefront-token="${storefrontToken}">
        <div class="pf-hero__grid">
          <div class="pf-mobile-header">
            <h2 class="pf-title">${name}</h2>
            <div class="pf-price-block">
              <span class="pf-price">$${heroPrice}</span>
              ${savingsHtml}
            </div>
          </div>
          <div class="pf-gallery-wrap">${galleryHtml}</div>
          <div class="pf-info">
            <h2 class="pf-title">${name}</h2>
            <div class="pf-price-block">
              <span class="pf-price">$${heroPrice}</span>
              ${savingsHtml}
            </div>
            ${materialToggleHtml}
            ${ctaHtml}
            <div class="pf-compat-badge" id="pf-compat-badge" hidden>
              <svg class="pf-compat-badge__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                <path d="M20 6L9 17l-5-5"/>
              </svg>
              <span class="pf-compat-badge__text"></span>
            </div>
            <div class="pf-kit-notice" id="pf-kit-notice" hidden>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/><text x="12" y="17" text-anchor="middle" fill="currentColor" stroke="none" font-size="14" font-weight="700" font-family="Georgia,serif" font-style="italic">i</text>
              </svg>
              <span id="pf-kit-notice-text">ELITECT kits do not include a frunk liner. The KAGU kit includes the frunk liner bundled with the floor mats.</span>
            </div>
            <details class="pf-kit-details" open>
              <summary class="pf-kit-summary">Complete Kit Includes (<span id="pf-kit-count">${skus.length}</span> items)</summary>
              <ul class="pf-kit-list" id="pf-kit-list"></ul>
            </details>
          </div>
        </div>
      </section>`;
  }

  // ---- Fallback / error UI ----

  _showFallbackUI(message) {
    const el = this._ref('fallback');
    el.hidden = false;
    el.innerHTML = `
      <div class="ai-intake__fallback-text">${esc(message)}</div>
      <div class="ai-intake__fallback-actions">
        <button class="ai-intake__retry" type="button">Start over</button>
      </div>
    `;
    requestAnimationFrame(() => el.classList.add('ai-intake--visible'));

    el.querySelector('.ai-intake__retry').addEventListener('click', () => {
      this._reset();
    });
  }

  _reset(targetSlot = 0) {
    this._hideAllPanels();
    this._selectedModel = null;
    this._selectedYear = null;
    this._selectedFitment = null;
    this._targetSlot = targetSlot;
    this._ref('text-mode').hidden = true;
    this._ref('text-mode').classList.remove('ai-intake--visible');
    const toggle = this._ref('toggle');
    const disclaimer = this._ref('disclaimer');
    if (toggle) toggle.hidden = true;
    if (disclaimer) disclaimer.hidden = false;
    const input = this._ref('input');
    if (input) { input.value = ''; input.disabled = false; }
    const submit = this._ref('submit');
    if (submit) submit.disabled = false;
    // Clear any inline display:none set by _renderInlineResult so the element shows
    this.style.display = '';
    this.hidden = false;
    this._showModelStep();
  }
}

customElements.define('ai-intake-bar', AiIntakeBar);
